import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import type { MarketplaceListing } from '@mprolab/sdk'
import {
  MARKETPLACE_ABI,
  MARKETPLACE_LISTING_V2_FRAGMENT,
  MULTICALL3_ABI,
  MULTICALL3_ADDRESS
} from './abi'

/**
 * Reads every listing from the marketplace contract via Multicall3 and
 * returns them decoded. The backend is out of the marketplace path
 * entirely (M4=B), so this is the only source of truth the UI uses.
 *
 * Strategy:
 *   1. Read `nextListingId()` (single call) — bounds the work.
 *   2. If next > 1, batch one `listings(id)` call per id 1..next-1
 *      into a Multicall3 `aggregate3` with `allowFailure=true`.
 *   3. Decode each result. Tokens that have moved out of the seller's
 *      wallet or had approval revoked still show as `active=true` in
 *      the contract — the caller (UI) decides whether to filter them.
 */
export async function readAllListings(params: {
  marketplaceAddress: string
  provider: ethers.providers.Provider
}): Promise<MarketplaceListing[]> {
  const market = new Contract(
    params.marketplaceAddress,
    MARKETPLACE_ABI as unknown as ethers.ContractInterface,
    params.provider
  )
  const next: ethers.BigNumber = await market.nextListingId()
  const lastId = next.toNumber() - 1
  if (lastId < 1) return []

  const ids = Array.from({ length: lastId }, (_, i) => i + 1)
  const calls = ids.map((id) => ({
    target: params.marketplaceAddress,
    allowFailure: true,
    callData: market.interface.encodeFunctionData('listings', [id])
  }))

  const multicall = new Contract(
    MULTICALL3_ADDRESS,
    MULTICALL3_ABI as unknown as ethers.ContractInterface,
    params.provider
  )

  const raw: Array<{ success: boolean; returnData: string }> =
    await multicall.callStatic.aggregate3(calls)

  // V2 returns eight fields (expiresAt appended), V1 seven. Try the V2
  // shape first and fall back, per row — so the same build reads both the
  // pre-upgrade and post-upgrade contract without a broken window.
  const v2Iface = new ethers.utils.Interface(
    MARKETPLACE_LISTING_V2_FRAGMENT as unknown as string[]
  )
  const decodeRow = (data: string) => {
    try {
      return v2Iface.decodeFunctionResult('listings', data)
    } catch {
      return market.interface.decodeFunctionResult('listings', data)
    }
  }

  const out: MarketplaceListing[] = []
  for (let i = 0; i < raw.length; i++) {
    if (!raw[i].success) continue
    try {
      const decoded = decodeRow(raw[i].returnData)
      out.push({
        listingId: String(ids[i]),
        seller: String(decoded.seller).toLowerCase(),
        nft: String(decoded.nft).toLowerCase(),
        tokenId: decoded.tokenId.toString(),
        priceWei: decoded.price.toString(),
        createdAt: Number(decoded.createdAt.toString()),
        active: Boolean(decoded.active),
        feeBps: Number(decoded.feeBps),
        expiresAt: decoded.expiresAt ? Number(decoded.expiresAt.toString()) : 0
      })
    } catch {
      // Skip undecodable rows (shouldn't happen with a correct ABI).
    }
  }
  return out
}

/** Single-listing read — used by the detail page and the buy flow. */
export async function readListing(params: {
  marketplaceAddress: string
  provider: ethers.providers.Provider
  listingId: string
}): Promise<MarketplaceListing | null> {
  const market = new Contract(
    params.marketplaceAddress,
    MARKETPLACE_ABI as unknown as ethers.ContractInterface,
    params.provider
  )
  const decoded = await market.listings(params.listingId)
  if (!decoded || decoded.seller === ethers.constants.AddressZero) return null
  return {
    listingId: String(params.listingId),
    seller: String(decoded.seller).toLowerCase(),
    nft: String(decoded.nft).toLowerCase(),
    tokenId: decoded.tokenId.toString(),
    priceWei: decoded.price.toString(),
    createdAt: Number(decoded.createdAt.toString()),
    active: Boolean(decoded.active),
    feeBps: Number(decoded.feeBps),
    expiresAt: decoded.expiresAt ? Number(decoded.expiresAt.toString()) : 0
  }
}
